import { useCallback } from "react"
import { useThemeContext } from "@/v2-ui/theme/theme.context"

/**
 * Creates a React hook for a CSS variable-based theme extension.
 *
 * Allows apps to define typed, named settings that map to CSS custom property
 * values. The values are stored in `overriddendCssVars` on the theme config,
 * so they are automatically persisted and applied by the theme system.
 *
 * @example
 * // Define the extension once at module level
 * export const useTableCellPadding = createThemeExtension({
 *   cssVar: "--ui-table-cell-padding",
 *   options: {
 *     compact: "0.25rem",
 *     default: "0.5rem",
 *     comfortable: "0.75rem",
 *   },
 *   defaultValue: "default",
 * })
 *
 * // Use it in any component inside ThemeProvider
 * const [padding, setPadding] = useTableCellPadding()
 */
export function createThemeExtension<
  TOptions extends Record<string, string>,
>(config: {
  /** The CSS custom property name this extension controls, e.g. `"--ui-table-cell-padding"`. */
  cssVar: `--${string}`
  /** Map of named option keys to their CSS custom property values. */
  options: TOptions
  /** The option key to use when no value has been explicitly set. */
  defaultValue: keyof TOptions & string
}) {
  type Value = keyof TOptions & string

  return function useThemeExtension(): readonly [
    Value,
    (value: Value) => void,
  ] {
    const { theme, setTheme } = useThemeContext()

    const currentCssValue = theme.overriddendCssVars?.[config.cssVar]
    const currentValue = (Object.entries(config.options).find(
      ([, v]) => v === currentCssValue,
    )?.[0] ?? config.defaultValue) as Value

    const setValue = useCallback(
      (value: Value) => {
        setTheme({
          ...theme,
          overriddendCssVars: {
            ...theme.overriddendCssVars,
            [config.cssVar]: config.options[value],
          },
        })
      },
      // config is a module-level constant, only theme/setTheme can change
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [theme, setTheme],
    )

    return [currentValue, setValue] as const
  }
}
